import { Fragment, useRef, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon, RefreshIcon } from '@heroicons/react/outline';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Pagination } from 'antd';
import { Loader } from '../../components/Loader';
import { fetchBillingConfigurationHistory } from '../../services';
import { formatShortDate, formatDateTime, formatCurrency } from '../../utils/billingUtils';

const T_PREFIX = 'billing.configuration_history';

const EVENT_TYPE_STYLES = {
    CONFIGURATION_CREATED: 'bg-green-100 text-green-800',
    CONFIGURATION_REPLACED: 'bg-blue-100 text-blue-800',
    CONFIGURATION_DELETED: 'bg-red-100 text-red-800',
    CONFIGURATION_DISABLED: 'bg-yellow-100 text-yellow-800',
    STATEMENT_GENERATED: 'bg-purple-100 text-purple-800',
    STATEMENT_RECALCULATED: 'bg-indigo-100 text-indigo-800',
    CUSTOM_SERVICE_ADDED: 'bg-green-100 text-green-800',
    CUSTOM_SERVICE_EDITED: 'bg-blue-100 text-blue-800',
    CUSTOM_SERVICE_REMOVED: 'bg-red-100 text-red-800',
};

const PER_PAGE = 15;

export const BillingConfigurationHistoryModal = ({
    open,
    setOpen,
    storeId,
    statementId = null,
    currency = 'MXN',
}) => {
    const { t, i18n } = useTranslation();
    const cancelButtonRef = useRef(null);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (open) setPage(1);
    }, [open]);

    const { data, isLoading, isFetching, refetch } = useQuery(
        ['billing_configuration_history', storeId, statementId, page],
        () => {
            const params = {
                store_id: storeId,
                page,
                per_page: PER_PAGE,
                order: 'desc',
            };
            if (statementId) params.statement_id = statementId;
            return fetchBillingConfigurationHistory(params);
        },
        {
            enabled: open && !!storeId,
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );

    const histories = data?.store_billing_configuration_histories || [];
    const pagination = data?.meta?.pagination || {};

    const formatDateParts = (dateStr) => {
        if (!dateStr) return { date: '-', time: '' };
        const locale = i18n.language === 'pt' ? 'pt-BR' : 'es-MX';
        const d = new Date(dateStr);
        return {
            date: d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' }),
            time: d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
        };
    };

    const renderConfigRange = (config) => {
        if (!config) return null;
        return (
            <span className="text-gray-400 ml-1">
                ({formatShortDate(config.valid_from)}
                {config.valid_to ? ` — ${formatShortDate(config.valid_to)}` : ''})
            </span>
        );
    };

    const renderDetails = (entry) => {
        const { event_type, details } = entry;
        if (!details) return <span className="text-gray-400">-</span>;

        switch (event_type) {
            case 'CONFIGURATION_CREATED':
                return (
                    <div className="space-y-0.5 text-sm">
                        {details.configuration?.valid_from && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.valid_from`)}:</span>{' '}
                                <span className="font-medium">{formatShortDate(details.configuration.valid_from)}</span>
                            </div>
                        )}
                        {details.services_count != null && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.services_count`)}:</span>{' '}
                                <span className="font-medium">{details.services_count}</span>
                            </div>
                        )}
                        {details.previous_configuration_id && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.previous_config`)}:</span>{' '}
                                <span className="font-medium">#{details.previous_configuration_id}</span>
                            </div>
                        )}
                    </div>
                );

            case 'CONFIGURATION_REPLACED':
                return (
                    <div className="space-y-0.5 text-sm">
                        {details.scenario && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.scenario`)}:</span>{' '}
                                <span className="font-medium">
                                    {t(`${T_PREFIX}.scenario_${details.scenario}`, details.scenario)}
                                </span>
                            </div>
                        )}
                        {details.from_configuration_id && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.from_config`)}:</span>{' '}
                                <span className="font-medium">#{details.from_configuration_id}</span>
                                {renderConfigRange(details.from_configuration)}
                            </div>
                        )}
                        {details.to_configuration && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.to_config`)}:</span>{' '}
                                <span className="font-medium">#{details.to_configuration.id}</span>
                                {renderConfigRange(details.to_configuration)}
                            </div>
                        )}
                    </div>
                );

            case 'CONFIGURATION_DELETED':
                return (
                    <div className="space-y-0.5 text-sm">
                        {details.reason && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.reason`)}:</span>{' '}
                                <span className="font-medium">
                                    {t(`${T_PREFIX}.reason_${details.reason}`, details.reason)}
                                </span>
                            </div>
                        )}
                        {details.deleted_configuration && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.deleted_config`)}:</span>{' '}
                                <span className="font-medium">#{details.deleted_configuration.id}</span>
                                {renderConfigRange(details.deleted_configuration)}
                            </div>
                        )}
                        {details.previous_configuration_id && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.previous_config`)}:</span>{' '}
                                <span className="font-medium">#{details.previous_configuration_id}</span>
                            </div>
                        )}
                        {details.previous_configuration_valid_to_restored_to !== undefined && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.restored_valid_to`)}:</span>{' '}
                                <span className="font-medium">
                                    {details.previous_configuration_valid_to_restored_to
                                        ? formatShortDate(details.previous_configuration_valid_to_restored_to)
                                        : t(`${T_PREFIX}.none`)}
                                </span>
                            </div>
                        )}
                        {details.replaced_by_configuration_id && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.replaced_by_config`)}:</span>{' '}
                                <span className="font-medium">#{details.replaced_by_configuration_id}</span>
                            </div>
                        )}
                        {details.deleted_during_replacement_of && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.deleted_during_replacement`)}:</span>{' '}
                                <span className="font-medium">#{details.deleted_during_replacement_of}</span>
                            </div>
                        )}
                    </div>
                );

            case 'CONFIGURATION_DISABLED':
                return (
                    <div className="space-y-0.5 text-sm">
                        {details.configuration && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.valid_from`)}:</span>{' '}
                                <span className="font-medium">{formatShortDate(details.configuration.valid_from)}</span>
                                {details.configuration.valid_to && (
                                    <>
                                        {' — '}
                                        <span className="text-gray-500">{t(`${T_PREFIX}.valid_to`)}:</span>{' '}
                                        <span className="font-medium">{formatShortDate(details.configuration.valid_to)}</span>
                                    </>
                                )}
                            </div>
                        )}
                        {details.future_configurations_deleted?.length > 0 && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.future_configs_deleted`)}:</span>{' '}
                                <span className="font-medium">
                                    {details.future_configurations_deleted.map(id => `#${id}`).join(', ')}
                                </span>
                            </div>
                        )}
                    </div>
                );

            case 'STATEMENT_GENERATED':
                return (
                    <div className="space-y-0.5 text-sm">
                        {details.configuration && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.config_id`)}:</span>{' '}
                                <span className="font-medium">#{details.configuration.id}</span>
                            </div>
                        )}
                        {details.statement?.cutoff_at && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.cutoff_date`)}:</span>{' '}
                                <span className="font-medium">{formatShortDate(details.statement.cutoff_at)}</span>
                            </div>
                        )}
                        {details.statement?.total_without_tax != null && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.amount`)}:</span>{' '}
                                <span className="font-medium">{formatCurrency(details.statement.total_without_tax, currency)}</span>
                            </div>
                        )}
                        {details.service_usages_count != null && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.usages_count`)}:</span>{' '}
                                <span className="font-medium">{details.service_usages_count}</span>
                            </div>
                        )}
                    </div>
                );

            case 'STATEMENT_RECALCULATED':
                return (
                    <div className="space-y-0.5 text-sm">
                        {details.to_configuration && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.to_config`)}:</span>{' '}
                                <span className="font-medium">#{details.to_configuration.id}</span>
                            </div>
                        )}
                        {details.from_configuration_ids?.length > 0 && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.from_config`)}:</span>{' '}
                                <span className="font-medium">
                                    {details.from_configuration_ids.map(id => `#${id}`).join(', ')}
                                </span>
                            </div>
                        )}
                        {details.previous_statement && details.statement && (
                            <div className="mt-1 space-y-0.5 border-t border-gray-100 pt-1">
                                <div>
                                    <span className="text-gray-500">{t(`${T_PREFIX}.total_before`)}:</span>{' '}
                                    <span className="font-medium">{formatCurrency(details.previous_statement.total_without_tax, currency)}</span>
                                    {' → '}
                                    <span className="text-gray-500">{t(`${T_PREFIX}.total_after`)}:</span>{' '}
                                    <span className="font-medium">{formatCurrency(details.statement.total_without_tax, currency)}</span>
                                </div>
                                {(parseFloat(details.previous_statement.billed_credit) !== 0 || parseFloat(details.statement.billed_credit) !== 0) && (
                                    <div>
                                        <span className="text-gray-500">{t(`${T_PREFIX}.credit_before`)}:</span>{' '}
                                        <span className="font-medium">{formatCurrency(details.previous_statement.billed_credit, currency)}</span>
                                        {' → '}
                                        <span className="text-gray-500">{t(`${T_PREFIX}.credit_after`)}:</span>{' '}
                                        <span className="font-medium">{formatCurrency(details.statement.billed_credit, currency)}</span>
                                    </div>
                                )}
                                <div>
                                    <span className="text-gray-500">{t(`${T_PREFIX}.tax_before`)}:</span>{' '}
                                    <span className="font-medium">{formatCurrency(details.previous_statement.total_tax, currency)}</span>
                                    {' → '}
                                    <span className="text-gray-500">{t(`${T_PREFIX}.tax_after`)}:</span>{' '}
                                    <span className="font-medium">{formatCurrency(details.statement.total_tax, currency)}</span>
                                </div>
                            </div>
                        )}
                        {details.custom_services_overrides_count > 0 && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.overrides_applied`)}:</span>{' '}
                                <span className="font-medium">{details.custom_services_overrides_count}</span>
                            </div>
                        )}
                    </div>
                );

            case 'CUSTOM_SERVICE_ADDED':
            case 'CUSTOM_SERVICE_EDITED':
            case 'CUSTOM_SERVICE_REMOVED':
                return (
                    <div className="space-y-0.5 text-sm">
                        {details.service_id && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.service_id`)}:</span>{' '}
                                <span className="font-medium">#{details.service_id}</span>
                            </div>
                        )}
                        {details.title && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.service_title`)}:</span>{' '}
                                <span className="font-medium">{details.title}</span>
                            </div>
                        )}
                        {details.billed_total_price != null && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.amount`)}:</span>{' '}
                                <span className={`font-medium ${parseFloat(details.billed_total_price) < 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatCurrency(details.billed_total_price, currency)}
                                </span>
                            </div>
                        )}
                        {details.description && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.description`)}:</span>{' '}
                                <span className="text-gray-700">{details.description}</span>
                            </div>
                        )}
                        {details.source && (
                            <div>
                                <span className="text-gray-500">{t(`${T_PREFIX}.source`)}:</span>{' '}
                                <span className="text-gray-700">
                                    {t(`${T_PREFIX}.source_${details.source}`, details.source)}
                                </span>
                            </div>
                        )}
                    </div>
                );

            default:
                return (
                    <pre className="text-xs text-gray-500 max-w-xs truncate">
                        {JSON.stringify(details)}
                    </pre>
                );
        }
    };

    const handleClose = () => {
        setOpen(false);
        setPage(1);
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="fixed z-10 inset-0 overflow-y-auto"
                initialFocus={cancelButtonRef}
                onClose={handleClose}
            >
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
                            <div className="flex justify-between items-center mb-4">
                                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                    {t(`${T_PREFIX}.title`)}
                                    {statementId && (
                                        <span className="text-sm font-normal text-gray-500 ml-2">
                                            — {t(`${T_PREFIX}.statement_id`)} #{statementId}
                                        </span>
                                    )}
                                </Dialog.Title>
                                <div className="flex items-center space-x-2">
                                    <button
                                        type="button"
                                        className="text-gray-400 hover:text-blue-600 transition-colors"
                                        onClick={() => refetch()}
                                        disabled={isFetching}
                                        title={t(`${T_PREFIX}.refresh`)}
                                    >
                                        <RefreshIcon className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
                                    </button>
                                    <button
                                        type="button"
                                        className="text-gray-400 hover:text-gray-500"
                                        onClick={handleClose}
                                        ref={cancelButtonRef}
                                    >
                                        <XIcon className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>

                            <div className="relative min-h-[200px]">
                                {isLoading ? (
                                    <div className="flex justify-center py-12">
                                        <Loader show={true} />
                                    </div>
                                ) : histories.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">{t(`${T_PREFIX}.no_history`)}</p>
                                    </div>
                                ) : (
                                    <>
                                        {isFetching && <div className="absolute inset-0 bg-white bg-opacity-50 z-10" />}
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            {t(`${T_PREFIX}.event`)}
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            {t(`${T_PREFIX}.details`)}
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            {t(`${T_PREFIX}.config_statement`)}
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            {t(`${T_PREFIX}.user`)}
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            {t(`${T_PREFIX}.date`)}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {histories.map((entry) => (
                                                        <tr key={entry.id}>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${EVENT_TYPE_STYLES[entry.event_type] || 'bg-gray-100 text-gray-800'}`}>
                                                                    {t(`${T_PREFIX}.event_${entry.event_type}`, entry.event_type)}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                {renderDetails(entry)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                <div className="space-y-0.5">
                                                                    {entry.store_billing_configuration_id && (
                                                                        <div>{t(`${T_PREFIX}.config_id`)} #{entry.store_billing_configuration_id}</div>
                                                                    )}
                                                                    {entry.statement_id && (
                                                                        <div>{t(`${T_PREFIX}.statement_id`)} #{entry.statement_id}</div>
                                                                    )}
                                                                    {!entry.store_billing_configuration_id && !entry.statement_id && '-'}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                {entry.performed_by
                                                                    ? entry.performed_by.email
                                                                    : t(`${T_PREFIX}.system_user`)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                {(() => {
                                                                    const { date, time } = formatDateParts(entry.created_at);
                                                                    return (
                                                                        <div>
                                                                            <div>{date}</div>
                                                                            {time && <div className="text-gray-400">{time}</div>}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {pagination.total_pages > 1 && (
                                            <div className="flex justify-end mt-4">
                                                <Pagination
                                                    size="small"
                                                    current={pagination.current_page}
                                                    total={pagination.total_count}
                                                    pageSize={PER_PAGE}
                                                    onChange={(p) => setPage(p)}
                                                    showSizeChanger={false}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
